import type { PlainWorkflowExecutionAction } from "@/queries/automation-queries";
import { WorkflowExecutionActionPhase } from "gitpod-next-api/gitpod/v1/workflow_pb";

export const canCancelAction = (_action: PlainWorkflowExecutionAction): boolean => {
    // TODO(AGENT-1092): Implement proper logic later.
    return true;
};

export const hasWorkflowExecutionActionFailed = (action: PlainWorkflowExecutionAction) => {
    return !!action.status?.failureMessage;
};

export const workflowExecutionActionPhaseLabel = (action: PlainWorkflowExecutionAction) => {
    const failed = hasWorkflowExecutionActionFailed(action);
    switch (action.status?.phase) {
        case WorkflowExecutionActionPhase.UNSPECIFIED:
            return "Unspecified";
        case WorkflowExecutionActionPhase.PENDING:
            return "Pending";
        case WorkflowExecutionActionPhase.RUNNING:
            return "Running";
        case WorkflowExecutionActionPhase.STOPPING:
            return "Stopping";
        case WorkflowExecutionActionPhase.STOPPED:
            return "Stopped";
        case WorkflowExecutionActionPhase.DELETING:
            return "Deleting";
        case WorkflowExecutionActionPhase.DELETED:
            return "Deleted";
        case WorkflowExecutionActionPhase.DONE:
            return failed ? "Failed" : "Done";
        default:
            return "Unknown";
    }
};

/** We're grouping actions based on the phase, but we don't need the full resolution, so we're mapping the phases to a subset of the action phases.  */
export enum ActionGroupType {
    Pending = "Pending",
    Active = "Active",
    Completed = "Completed",
    Stopped = "Stopped",
    Failed = "Failed",
    Deleted = "Deleted",
    Unknown = "Unknown",
}

export type ActionGroup = {
    type: ActionGroupType;
    actions: PlainWorkflowExecutionAction[];
};

const getActionGroupType = (action: PlainWorkflowExecutionAction): ActionGroupType => {
    const phase = action.status?.phase ?? WorkflowExecutionActionPhase.UNSPECIFIED;
    const failed = hasWorkflowExecutionActionFailed(action);

    switch (phase) {
        case WorkflowExecutionActionPhase.RUNNING:
            return ActionGroupType.Active;
        case WorkflowExecutionActionPhase.PENDING:
            return ActionGroupType.Pending;
        case WorkflowExecutionActionPhase.DONE:
            return failed ? ActionGroupType.Failed : ActionGroupType.Completed;
        case WorkflowExecutionActionPhase.STOPPING:
        case WorkflowExecutionActionPhase.STOPPED:
            return ActionGroupType.Stopped;
        case WorkflowExecutionActionPhase.DELETED:
        case WorkflowExecutionActionPhase.DELETING:
            return ActionGroupType.Deleted;
        case WorkflowExecutionActionPhase.UNSPECIFIED:
        default:
            return ActionGroupType.Unknown;
    }
};

export const groupActions = (actions: PlainWorkflowExecutionAction[]): Map<ActionGroupType, ActionGroup> => {
    const grouped = new Map<ActionGroupType, ActionGroup>();

    const sorted = sortActionsByStartTime(actions);
    for (const action of sorted) {
        const groupType = getActionGroupType(action);
        const existing = grouped.get(groupType);

        if (existing) {
            existing.actions.push(action);
        } else {
            grouped.set(groupType, {
                type: groupType,
                actions: [action],
            });
        }
    }

    return grouped;
};

const sortActionsByStartTime = (actions: PlainWorkflowExecutionAction[]): PlainWorkflowExecutionAction[] => {
    return actions.toSorted((a, b) => {
        const aTime = a.metadata?.startedAt;
        const bTime = b.metadata?.startedAt;

        // Handle missing timestamps - put them at the end
        if (!aTime && !bTime) return 0;
        if (!aTime) return 1;
        if (!bTime) return -1;

        // Compare timestamps
        const aSeconds = aTime.seconds;
        const bSeconds = bTime.seconds;
        if (aSeconds !== bSeconds) {
            return Number(aSeconds - bSeconds);
        }

        // If seconds are equal, compare nanos
        return aTime.nanos - bTime.nanos;
    });
};
